import { KitType } from '@repo/types';
import { AgeGroup, getAgeGroup } from '@repo/utils';
import {
  AudienceFeedbackOverlay,
  ContainerSizes,
  FeedbackWidget,
  PageContainer,
  Typography,
} from 'components';
import AgeGroupTransitionPopup from 'components/AgeGroupTransitionPopup/AgeGroupTransitionPopup';
import TwoColumnGrid from 'components/TwoColumnGrid/TwoColumnGrid';
import VersionPill from 'components/VersionPill/VersionPill';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import { useAnalyticsSource } from 'contexts/analytics/useAnalyticsSource';
import { format } from 'date-fns';
import { getShouldDisplaySeeding, parseDate } from 'helpers';
import { useCurrentKitAndTinyAccount } from 'hooks/useCurrentKitAndTinyAccount';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import useFeature from 'services/features/useFeature';
import {
  getLatestKitForTinyAccountId,
  getResultsReadyKitForTinyAccountId,
  getSortedKitsForTinyAccountId,
} from 'store/kits';
import { getHasModalOpened } from 'store/modal';
import { FeedbackPageType } from 'types/Feedback';
import { InsightEvaluationSection } from 'types/Insights';
import PendingResultsBanner from 'views/ActionPlan/PendingResultsBanner';
import FirstLookPopup from './FirstLook/FirstLookPopup';
import { shouldShowFirstLook } from './FirstLook/helpers';
import {
  FirstLookFeatureFlags,
  useFirstLookFeature,
} from './FirstLook/helpers/shouldShowFirstLook';
import { MainFindings } from './MainFindings/MainFindings';
import { PractitionerNoteAlert } from './PractitionerNoteAlert/PractitionerNoteAlert';
import styles from './Summary.module.css';
import { ActionPlanCard } from './SummaryCards/ActionPlanCard/ActionPlanCard';
import { ConditionMetricsGridCard } from './SummaryCards/ConditionMetricsGridCard/ConditionMetricsGridCard';
import { GutResilienceCard } from './SummaryCards/GutResilienceCard/GutResilienceCard';
import { GutTypeCard } from './SummaryCards/GutTypeCard/GutTypeCard';
import GutXAxisCard from './SummaryCards/GutXAxisCard';
import { MatIndexCard } from './SummaryCards/MatIndexCard/MatIndexCard';
import MicrobiomeAgeCard from './SummaryCards/MicrobiomeAgeCard/MicrobiomeAgeCard';
import { MicrobiomeScoreSummaryCard } from './SummaryCards/MicrobiomeScoreSummaryCard/MicrobiomeScoreSummaryCard';
import { MicrobiomeSeedingCard } from './SummaryCards/MicrobiomeSeedingCard/MicrobiomeSeedingCard';
import { ReplayFirstLookCard } from './SummaryCards/ReplayFirstLookCard/ReplayFirstLookCard';
import { ScheduleConsultSidebarCard } from './SummaryCards/ScheduleConsultSidebarCard/ScheduleConsultSidebarCard';
import { SidebarActionCard } from './SummaryCards/SidebarActionCard/SidebarActionCard';
import VersionUpdateBanner from './VersionUpdateBanner/VersionUpdateBanner';
import YourCategoryScores from './YourCategoryScores';

export type InsightSectionWithEvaluationGroupCounts =
  InsightEvaluationSection & {
    great: number;
    okay: number;
    support: number;
  };

export type ConditionMetricGroup = {
  id: number;
  condition: string;
  condition_display_name: string;
  good: number;
  okay: number;
  improve: number;
  support: number;
  metrics: {
    metric_id: number;
    name: string;
    evaluation_color: string;
    user_value: number;
    health_metrics_barscale_percentage: number;
  }[];
};

const visitKey = `summary_visit_count`;

const Summary = () => {
  const { currentKit, currentKitId, currentTinyAccountId } =
    useCurrentKitAndTinyAccount();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { enabled: displayFirstLookPopup, cutoffDate } =
    useFirstLookFeature('first_look_popup');
  const { isFeatureEnabled: displayFirstLookFeedback } = useFeature(
    'first_look_feedback',
  );
  const { isFeatureEnabled: sidebarV2Enabled } = useFeature(
    'results_summary_sidebar_v2',
  );
  const { isFeatureEnabled: gutXAxisEnabled } = useFeature('gut_x_axis');
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

  const firstLookModalIdentifier = {
    name: 'firstLook',
    props: {},
  };
  const isFirstLookDismissed = useSelector(
    getHasModalOpened(firstLookModalIdentifier),
  );

  const displaySeeding = useMemo(
    () => (currentKit ? getShouldDisplaySeeding(currentKit) : false),
    [currentKit],
  );

  const isToddler =
    currentKit?.age_at_sampling && currentKit.age_at_sampling <= 1095;

  const allKitsForAccount = useSelector(
    getSortedKitsForTinyAccountId(currentTinyAccountId),
  );

  const latestKit = useSelector(
    getLatestKitForTinyAccountId(currentTinyAccountId),
  );
  const resultsReadyKit = useSelector(
    getResultsReadyKitForTinyAccountId(currentTinyAccountId),
  );

  const showPendingBanner =
    !!resultsReadyKit?.id &&
    currentKitId === resultsReadyKit.id &&
    !!latestKit &&
    currentKitId !== latestKit.id &&
    !!latestKit.is_resample &&
    !latestKit.date_results_ready;

  const latestKitDateFormatted = latestKit?.sample_date
    ? format(parseDate(latestKit.sample_date), 'MMMM d, yyyy')
    : undefined;
  const currentKitDateFormatted = currentKit?.sample_date
    ? format(parseDate(currentKit.sample_date), 'MMMM d, yyyy')
    : undefined;

  const secondLatestKit = currentKit?.is_resample // is_resample is a way to know if there are more than two kits for an account
    ? allKitsForAccount?.[1]
    : undefined;

  const secondLatestKitAgeGroup = secondLatestKit
    ? getAgeGroup(secondLatestKit.age_at_sampling ?? 0)
    : undefined;

  const kitAgeGroup = getAgeGroup(currentKit?.age_at_sampling ?? 0);
  const transitionModalAgeGroups = [AgeGroup.TODDLER, AgeGroup.CHILD];

  const shouldShowAgeGroupTransitionPopup =
    !!currentKit?.is_resample && // No need to check for the second latest kit if there is no resample
    kitAgeGroup !== secondLatestKitAgeGroup &&
    transitionModalAgeGroups.includes(kitAgeGroup);

  const featureFlags: FirstLookFeatureFlags = {
    cutoffDate,
  };

  const shouldShowFirstLookPopup = useMemo(
    () =>
      displayFirstLookPopup && shouldShowFirstLook(currentKit, featureFlags),
    [displayFirstLookPopup, currentKit, featureFlags],
  );

  const { trackEvent } = useAnalytics();
  const resultsViewedSource = useAnalyticsSource<
    'sidebar' | 'homepage_cta' | 'direct'
  >();
  useEffect(() => {
    if (!currentKitId) return;
    trackEvent('Results Viewed', {
      source: resultsViewedSource,
      test_type: currentKit?.sampling_type,
    });
  }, [
    currentKitId,
    currentKit?.sampling_type,
    resultsViewedSource,
    trackEvent,
  ]);

  useEffect(() => {
    if (!currentKitId || !isFirstLookDismissed) return;

    const currentVisitCount = parseInt(
      sessionStorage.getItem(visitKey) || '0',
      10,
    );
    const newVisitCount = currentVisitCount + 1;
    sessionStorage.setItem(visitKey, newVisitCount.toString());

    if (newVisitCount < 2) return;

    const t = window.setTimeout(() => {
      setIsFeedbackOpen(true);
    }, 3000);

    return () => {
      window.clearTimeout(t);
    };
  }, [currentKitId, isFirstLookDismissed]);

  if (!currentKitId) return <></>;

  return (
    <PageContainer size={ContainerSizes.LG}>
      {displayFirstLookFeedback ? (
        <AudienceFeedbackOverlay
          kitId={currentKitId}
          isOpen={isFeedbackOpen}
          onOpenChange={setIsFeedbackOpen}
          feedbackType='results_summary'
          questions={['NpsQuestion', 'NpsTextQuestion']}
        />
      ) : null}
      <VersionUpdateBanner />
      <PractitionerNoteAlert />
      <TwoColumnGrid>
        {shouldShowAgeGroupTransitionPopup ? (
          <AgeGroupTransitionPopup
            newAgeGroup={kitAgeGroup as 'toddler' | 'child'}
          />
        ) : null}
        {shouldShowFirstLookPopup ? <FirstLookPopup /> : null}

        <TwoColumnGrid.Wide>
          <div className='flex flex-column gap-4'>
            {showPendingBanner &&
              latestKitDateFormatted &&
              currentKitDateFormatted && (
                <PendingResultsBanner
                  latestKitDate={latestKitDateFormatted}
                  currentKitDate={currentKitDateFormatted}
                  pageType='results'
                />
              )}
            <MicrobiomeScoreSummaryCard />
            {!sidebarV2Enabled && shouldShowFirstLookPopup ? (
              <ReplayFirstLookCard />
            ) : null}
            <YourCategoryScores featureFlagFallback={<MainFindings />} />
            {currentKit?.sampling_type === KitType.STOOL && gutXAxisEnabled ? (
              <GutXAxisCard />
            ) : (
              <ConditionMetricsGridCard />
            )}
            {!sidebarV2Enabled && <ActionPlanCard />}

            <div className='hide-on-mobile'>
              <FeedbackWidget
                kitId={currentKitId}
                pageType={FeedbackPageType.SUMMARY}
                pageId={0}
              />
            </div>
          </div>
        </TwoColumnGrid.Wide>
        <TwoColumnGrid.Slim isSticky={!sidebarV2Enabled}>
          <div className={styles.slimSection}>
            {sidebarV2Enabled ? (
              <>
                <GutResilienceCard />
                <GutTypeCard />
                <ScheduleConsultSidebarCard />
                {shouldShowFirstLookPopup ? <ReplayFirstLookCard /> : null}
                {isToddler ? <MatIndexCard /> : null}
                {displaySeeding ? <MicrobiomeSeedingCard /> : null}
              </>
            ) : isToddler ? (
              <>
                <MatIndexCard />
                <GutResilienceCard />
                {displaySeeding ? <MicrobiomeSeedingCard /> : null}
                <GutTypeCard />
              </>
            ) : (
              <>
                <SidebarActionCard />
                <GutResilienceCard />
                {displaySeeding ? <MicrobiomeSeedingCard /> : null}
                <GutTypeCard />
              </>
            )}
            <MicrobiomeAgeCard />
            <div className='hide-on-desktop'>
              <FeedbackWidget
                kitId={currentKitId}
                pageType={FeedbackPageType.SUMMARY}
                pageId={0}
              />
            </div>
          </div>
        </TwoColumnGrid.Slim>
      </TwoColumnGrid>
      {currentKit?.sci_initial_version && (
        <div className='hide-on-desktop'>
          <div className='flex gap-2'>
            <Typography variant='label' className='my-auto'>
              System version
            </Typography>
            <VersionPill />
          </div>
        </div>
      )}
    </PageContainer>
  );
};

export default Summary;
